<template>
	<div class="SearchableCheckboxFilter">
		<Multiselect
			:options="options"
			:model-value="internalValue"
			:mode="MultiselectMode.TAGS"
			:searchable="true"
			:can-clear="true"
			:max-height="'18rem'"
			:placeholder="placeholder"
			:description="description"
			:warning="warning"
			:is-location-filter="isLocationFilter"
			@changeContinent="emit('changeContinent')"
			@updateValues="handleMultiselectUpdate"
		/>

		<div class="List">
			<CheckBoxSelectItem
				v-for="entry in items"
				:key="entry.item.value"
				:item="entry.item"
				:subItems="entry.subItems"
				@selectionChange="(state, changedItem) => handleCheckboxChange(changedItem || entry.item, state)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, type PropType } from 'vue';
import Multiselect from '@components/Multiselect/Multiselect.vue';
import CheckBoxSelectItem from '@components/CheckBoxSelectItem/CheckBoxSelectItem.vue';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { ItemEntry } from '@app/interfaces/useMultiselect.interface';
import { describe } from 'node:test';
import { warn } from 'console';

const props = defineProps({
	items: {
		type: Array as PropType<ItemEntry[]>,
		required: true
	},
	options: {
		type: Array as PropType<IMultiselectOption[]>,
		required: true
	},
	modelValue: {
		type: Array as PropType<(string | number)[]>,
		required: true
	},
	placeholder: {
		type: String,
		default: 'Search'
	},
	isLocationFilter: {
		type: Boolean,
		default: false
	},
	description: {
		type: String,
		default: ''
	},
	warning: {
		type: String,
		default: ''
	}
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: (string | number)[]): void;
	(e: 'select', value: string | number, selected: boolean): void;
	(e: 'changeContinent'): void;
}>();

const internalValue = ref<(string | number)[]>([...props.modelValue]);

watch(() => props.modelValue, (val) => {
	internalValue.value = [...val];
}, { deep: true });

const handleCheckboxChange = (item: ISelectItem, state: string): void => {
	const selected = state === SelectListItemStateType.SELECTED;

	if (selected && !internalValue.value.includes(item.value)) {
		internalValue.value = [...internalValue.value, item.value];
	} else if (!selected) {
		internalValue.value = internalValue.value.filter((v) => v !== item.value);
	}

	emit('select', item.value, selected);
	emit('update:modelValue', [...internalValue.value]);
};

const handleMultiselectUpdate = (value: (string | number)[] | string | number | null): void => {
	const normalized = Array.isArray(value) ? value : value ? [value] : [];
	const prevSet = new Set(internalValue.value);
	const nextSet = new Set(normalized);

	const added = normalized.filter((v) => !prevSet.has(v));
	const removed = internalValue.value.filter((v) => !nextSet.has(v));

	for (const add of added) emit('select', add, true);
	for (const rem of removed) emit('select', rem, false);

	internalValue.value = normalized;
	emit('update:modelValue', [...internalValue.value]);
};
</script>

<style scoped lang="scss">
.SearchableCheckboxFilter {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	padding: 0 2px;	// required for outline not to be cut off

	.List {
		display: flex;
		flex-direction: column;
		max-height: 14rem;
		overflow: auto;
		scrollbar-width: none;
		padding: 2px 2px 0 0;	// required for outline not to be cut off

		&::-webkit-scrollbar {
			display: none;
		}
	}
}
</style>
